import { cn, cva } from 'noddi-ui-common';
import type { ReactNode } from 'react';

type NavioTimelineStep = {
  key: string;
  title: string;
  description?: ReactNode;
};

type StepState = 'reached' | 'next' | 'upcoming';

const dotVariants = cva('size-3 shrink-0 rounded-full border-2', {
  variants: {
    state: {
      reached: 'border-primary bg-primary',
      next: 'border-primary bg-card',
      upcoming: 'border-darkPurple50 bg-card'
    }
  }
});

const barVariants = cva('h-1 rounded-full', {
  variants: {
    state: {
      reached: 'bg-primary',
      next: 'bg-darkPurple40',
      upcoming: 'bg-muted'
    }
  }
});

const barTitleVariants = cva('text-sm', {
  variants: {
    state: {
      reached: 'font-medium text-primary',
      next: 'font-semibold text-primary',
      upcoming: 'font-medium text-muted-foreground'
    }
  }
});

type Props = {
  steps: NavioTimelineStep[];
  /** Index of the step the process is at. Earlier steps read as done, the one after it as next. */
  currentStep: number;
  /**
   * `dots` (default): milestones on one connecting line, stacking below `md`. `bars`: a filled segment per
   * milestone with its title under it, always in one row — a compact progress strip inside a card.
   */
  variant?: 'dots' | 'bars';
  className?: string;
};

/**
 * Horizontal milestone track for a process that runs on its own — a dot per milestone on one
 * connecting line (or a bar per milestone with `variant='bars'`), title and time under each. Unlike `NoddiStepper` nothing here is a user step:
 * no numbers, no check icons, no step content. Stacks vertically below `md`.
 */
export const NavioTimeline = ({ steps, currentStep, variant = 'dots', className }: Props) => {
  if (variant === 'bars') {
    return (
      <ol className={cn('flex gap-2', className)}>
        {steps.map((step, index) => {
          const state = stepState(index, currentStep);

          return (
            <li
              key={step.key}
              aria-current={index === currentStep ? 'step' : undefined}
              className='flex min-w-0 flex-1 flex-col gap-2'
            >
              <span className={barVariants({ state })} />
              <span className={barTitleVariants({ state })}>{step.title}</span>
              {step.description && <span className='text-sm text-darkPurple70'>{step.description}</span>}
            </li>
          );
        })}
      </ol>
    );
  }

  return <DotTimeline steps={steps} currentStep={currentStep} className={className} />;
};

const DotTimeline = ({ steps, currentStep, className }: Omit<Props, 'variant'>) => (
  <ol className={cn('flex flex-col gap-4 md:flex-row md:gap-0', className)}>
    {steps.map((step, index) => {
      const state = stepState(index, currentStep);
      const isLast = index === steps.length - 1;

      return (
        <li
          key={step.key}
          aria-current={index === currentStep ? 'step' : undefined}
          className='flex min-w-0 flex-1 flex-row gap-3 md:flex-col md:gap-2'
        >
          <div className='flex items-center pt-1 md:pt-0'>
            <span className={dotVariants({ state })} />
            {!isLast && <span className='hidden h-0.5 flex-1 bg-darkPurple40 md:block' />}
          </div>
          <div className='flex min-w-0 flex-col gap-1 md:pr-4'>
            <span className='text-sm font-semibold text-primary'>{step.title}</span>
            {step.description && <span className='text-sm text-darkPurple70'>{step.description}</span>}
          </div>
        </li>
      );
    })}
  </ol>
);

const stepState = (index: number, currentStep: number): StepState =>
  index <= currentStep ? 'reached' : index === currentStep + 1 ? 'next' : 'upcoming';
